<script setup lang="ts">
import { computed, ref } from 'vue';
import { useClassStore, useQuizStore, useUserStore } from '../../../../../stores';
import { UserRole } from '../../../../../types/user';
import { AssessmentAnswerDetail, AssessmentChoice, AssessmentQuestion } from '../../../../../types/assessment';

import ButtonV2 from '../../../../General/ButtonV2.vue';
import RenderQuestion from '../components/QuizQuestionContent.vue';
import { AttendStatus } from '../../../../../types/class';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../../../configs/text';
import Katex from '../../../../General/Katex.vue';
import { QuestionState } from '../../../../../types/quiz';

interface Props {
    question: AssessmentQuestion;
    disabled?: boolean;
    showAnswer?: boolean;
    showSubmission?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    showAnswer: false,
    disabled: false,
    showSubmission: false,
});

const emit = defineEmits(['submit']);
const { t } = useI18n();
const userStore = useUserStore();
const classStore = useClassStore();
const quizStore = useQuizStore();

const submitedAnswer = computed<AssessmentAnswerDetail[]>(() => {
    const question = classStore.currentStudent?.quizQuestion?.find((question) => question.id === props.question.id);
    return question ? question.answer : [];
});

const answers = ref<string[]>(submitedAnswer.value?.map((item) => item.answer));

const isSubmitted = computed(() => {
    return classStore.currentStudent?.quizQuestion?.some((answer) => answer.id === props.question.id) || false;
});

const allAnswersEmpty = computed(() => answers.value.every((answer) => answer.trim() === ''));

function showLine(index: number) {
    if (userStore.userRole === UserRole.teacher) {
        if (!props.showAnswer) return true;
        return false;
    }

    if (userStore.userRole === UserRole.student) {
        return answers.value[index]?.trim() !== '';
    }
}

function selectedCount(choice: AssessmentChoice) {
    const corectAnswers = choice.content.split(',').map((ans) => ans.trim().toLowerCase());
    return classStore.selectedClassDetails?.students?.reduce((count, student) => {
        return count + (student.status == AttendStatus.attended && student.quizQuestion?.some((question) => question.answer.find((a) => a.isCorrect === 1 && corectAnswers.includes(a.answer.toLowerCase()))) ? 1 : 0);
    }, 0);
};
</script>
<template>
    <Katex class="flex gap-1 px-1 py-1 md:px-3 lg:px-6 lg:py-2 md:gap-3 flex-col w-full h-full justify-between overflow-auto md:overflow-hidden">
        <RenderQuestion class="grow max-h-3/5" layout="horizontal" :question="question" :showAnswer="showAnswer" />
        <div class="flex grow shrink-0 overflow-auto gap-3 flex-col">
            <div class="w-full flex flex-col gap-3 bg-white border-[10px] dark:border-[2px] border-violet-400 dark:border-slate-300 shadow-[0_6px_0] dark:shadow-[inset_0_-6px_0] shadow-indigo-500 dark:shadow-sky-100 rounded-lg p-3">
                <div class="flex gap-3 items-center" v-for="(choice, index) in props.question.choices" :key="choice.id">
                    <label class="size-[30px] flex items-center justify-center rounded-full font-bold  text-white dark:text-zinc-800 bg-indigo-500 dark:bg-yellow-500 dark:border-1 dark:border dark:border-yellow-600" :class="[
                        { 'bg-success dark:!border-lime-500 dark:!bg-lime-200': (userStore.userRole === UserRole.teacher && props.showAnswer) || (userStore.userRole === UserRole.student && props.showAnswer && submitedAnswer[index]?.isCorrect == 1) },
                        { 'bg-danger dark:!border-red-600 dark:!bg-red-400': userStore.userRole === UserRole.student && props.showAnswer && (submitedAnswer[index]?.isCorrect == 0 || !answers[index]) }
                    ]" :for="`answer_${index}`">{{ index + 1 }}</label>
                    <div class="flex-1 rounded-md relative text-2xl" :class="[{ 'before:absolute before:w-full before:h-[1px] before:bg-indigo-500 before:dark:bg-zinc-800 before:top-3/4': showLine(index) }]">

                        <input type="text" :id="`answer_${index}`" class="block w-full p-1 disabled:bg-white focus:border-none focus:outline-none text-xl placeholder-violet-400 text-violet-500 dark:text-black"
                            :class="[{ '!text-success': (props.showAnswer) }]" :disabled="props.disabled" :value="props.showAnswer ? choice.content.split(',')[0] : ''" v-if="userStore.userRole === UserRole.teacher" />

                        <input type="text" :id="`answer_${index}`" class="block w-full p-1 disabled:bg-none focus:border-none focus:outline-none text-xl placeholder-violet-400 text-violet-500 dark:text-black" :class="[
                            { '!text-success': props.showAnswer && submitedAnswer[index]?.isCorrect == 1 },
                            { '!text-danger': props.showAnswer && submitedAnswer[index]?.isCorrect == 0 }]" :disabled="props.disabled || isSubmitted" v-model="answers[index]" v-if="userStore.userRole === UserRole.student" />
                    </div>

                    <template v-if="props.showAnswer && userStore.userRole === UserRole.student">
                        <!-- correct -->
                        <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="(submitedAnswer[index]?.isCorrect == 1)">
                            <path d="M9.40628 24L0 13.7283L3.38419 10.0326L9.40628 16.6087L24.6157 0L28 3.69565L9.40628 24Z" fill="#48BD0D" />
                        </svg>

                        <!-- incorrect -->
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" v-else>
                            <path d="M4.41289 0.00140702L0 4.41406L23.5871 27.9999L28 23.5873L4.41289 0.00140702Z" fill="#EC2E49" />
                            <path d="M23.5871 0.000113563L0 23.5859L4.41283 27.9985L27.9999 4.41271L23.5871 0.000113563Z" fill="#EC2E49" />
                        </svg>

                    </template>
                    <template v-if="userStore.userRole === UserRole.teacher && props.showSubmission">
                        <span class="flex gap-1 items-start text-success text-xl font-bold">
                            {{ selectedCount(choice) }}
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M1 20V17.2C1 16.6334 1.146 16.1127 1.438 15.638C1.73 15.1634 2.11733 14.8007 2.6 14.55C3.63333 14.0334 4.68333 13.646 5.75 13.388C6.81667 13.13 7.9 13.0007 9 13C10.1 12.9994 11.1833 13.1287 12.25 13.388C13.3167 13.6474 14.3667 14.0347 15.4 14.55C15.8833 14.8 16.271 15.1627 16.563 15.638C16.855 16.1134 17.0007 16.634 17 17.2V20H1ZM19 20V17C19 16.2667 18.796 15.5624 18.388 14.887C17.98 14.2117 17.4007 13.6327 16.65 13.15C17.5 13.25 18.3 13.421 19.05 13.663C19.8 13.905 20.5 14.2007 21.15 14.55C21.75 14.8834 22.2083 15.254 22.525 15.662C22.8417 16.07 23 16.516 23 17V20H19ZM9 12C7.9 12 6.95833 11.6084 6.175 10.825C5.39167 10.0417 5 9.10003 5 8.00003C5 6.90003 5.39167 5.95836 6.175 5.17503C6.95833 4.39169 7.9 4.00003 9 4.00003C10.1 4.00003 11.0417 4.39169 11.825 5.17503C12.6083 5.95836 13 6.90003 13 8.00003C13 9.10003 12.6083 10.0417 11.825 10.825C11.0417 11.6084 10.1 12 9 12ZM19 8.00003C19 9.10003 18.6083 10.0417 17.825 10.825C17.0417 11.6084 16.1 12 15 12C14.8167 12 14.5833 11.9794 14.3 11.938C14.0167 11.8967 13.7833 11.8507 13.6 11.8C14.05 11.2667 14.396 10.675 14.638 10.025C14.88 9.37503 15.0007 8.70003 15 8.00003C14.9993 7.30003 14.8787 6.62503 14.638 5.97503C14.3973 5.32503 14.0513 4.73336 13.6 4.20003C13.8333 4.11669 14.0667 4.06236 14.3 4.03703C14.5333 4.01169 14.7667 3.99936 15 4.00003C16.1 4.00003 17.0417 4.39169 17.825 5.17503C18.6083 5.95836 19 6.90003 19 8.00003Z"
                                    fill="#48BD0D" />
                            </svg>
                        </span>
                    </template>
                </div>
            </div>
        </div>
        <div class="w-full flex items-center justify-center" v-if="userStore.userRole === UserRole.student && quizStore.questionState?.state === QuestionState.ON_GOING && !disabled && !isSubmitted">
            <ButtonV2 color="danger" class="min-w-32" animation @click="$emit('submit', answers)" :disabled="allAnswersEmpty">{{
                t(TEXT.submit_btn) }}</ButtonV2>
        </div>
        <div class="w-full first-letter:uppercase" v-if="question.explanation && question.explanation.length > 0 && showAnswer && userStore.userRole === UserRole.student">
            <span class="font-bold">{{ t(TEXT.explanation) }}:</span>
            <div v-html="question.explanation"></div>
        </div>
    </Katex>
</template>